"use client";

import { useEffect, useState } from "react";

/* One shared promise for the whole page: resolves after the first paint and
   the next idle period. Components gate their lazy GSAP children on it so the
   motion chunk downloads after LCP instead of competing with it. */
let afterPaint: Promise<void> | null = null;

function whenAfterPaint(): Promise<void> {
  if (!afterPaint) {
    afterPaint = new Promise((resolve) => {
      const idle = () => {
        if ("requestIdleCallback" in window) window.requestIdleCallback(() => resolve(), { timeout: 2000 });
        else setTimeout(resolve, 200);
      };
      const start = () => requestAnimationFrame(() => requestAnimationFrame(idle));
      if (document.readyState === "complete") start();
      else window.addEventListener("load", start, { once: true });
    });
  }
  return afterPaint;
}

function nextIdle(): Promise<void> {
  return new Promise((resolve) => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(() => resolve(), { timeout: 1000 });
    else setTimeout(resolve, 50);
  });
}

/**
 * `order` pushes a consumer N extra idle periods back, so motion components
 * that share the GSAP chunk initialise in separate tasks instead of one long
 * one (the hero goes first at 0; below-the-fold work follows).
 */
export function useAfterPaint(order = 0): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      await whenAfterPaint();
      for (let i = 0; i < order; i++) await nextIdle();
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [order]);
  return ready;
}
